import type { Item, Level, Module, Vec2, Wall } from '../core/hp/types';
import { applyWallProperties, validateLevel, type EditResult, type WallProperties, type OpeningProperties } from '../core/geometry/edit';
import { applyItemProperties, itemDraft, itemDraftValues, type ItemDraft, type ItemProperties } from './itemProperties';
import { validateOpeningDesign } from './openingProperties';
import { syncOpeningTwins } from '../core/geometry/openingTwins';
import { carryCurtains } from './curtains';
import { replacementPlace, shownReplacement, type ShownPlace } from './catalogPlacement';
import type { CatalogItem } from './catalog';
import { resizeRoom, type RoomSizeValues } from './roomSize';
import { trimRoomWalls, type Trim } from './contextCommands';
import { t } from '../i18n/I18n';

/** The lowest plinth or molding the inspector takes, in metres; the highest is its wall's height (`GetMaxLength`). */
export const TRIM_HEIGHT_MIN = .01;
/** `place`: the place the inspector showed for a replacement of another type, committed at the values' point, height and turn. */
export type PropertyEdit = { kind: 'item'; id: string; values: ItemProperties; replacement?: CatalogItem | null; place?: ShownPlace }
  | { kind: 'wall'; id: string; values: WallProperties }
  | { kind: 'opening'; id: string; values: OpeningProperties }
  /** A room by its index, as a string: the inspector's draft is keyed `kind:id`. */
  | { kind: 'room'; id: string; values: RoomSizeValues }
  /** A wall's plinth or ceiling molding (`id` is the wall's): its height, which every one of its kind in the room takes. */
  | { kind: 'trim'; id: string; trim: Trim; values: { height: number } };
/**
 * The same validated edit prepares presentation and commits the document. An opening's other copies
 * follow it (`syncOpeningTwins`), paired as they stand in `pairs`: the level itself, or the level a
 * gesture started from, so a copy that had to go midway comes back. Its curtains follow it as they
 * hung in `pairs` (`carryCurtains`).
 */
export function editProperties(level: Level, edit: PropertyEdit, pairs: Level = level): EditResult {
  try {
    let next: Level;
    if (edit.kind === 'item') {
      const item = level.items.find(value => value.id === edit.id);
      if (!item) return { error: t('#GeometryItemNotFound') };
      const updated = structuredClone(item);
      applyItemProperties(updated, edit.values, edit.replacement);
      if (edit.replacement) {
        const place = edit.place ? { placement: edit.place.placement, wallId: edit.place.wallId } : replacementPlace(level, item, edit.replacement, { ...updated, position: item.position });
        if (place) Object.assign(updated, place);
      }
      const wall = updated.placement === 'wall' ? level.walls.find(value => value.id === updated.wallId) : undefined;
      if (wall && (updated.position.x !== item.position.x || updated.position.z !== item.position.z)) updated.position = withinWall(wall, updated.position, updated.size.w);
      next = { ...level, items: level.items.map(value => value.id === edit.id ? updated : value) };
    } else if (edit.kind === 'room') {
      const result = resizeRoom(level, Number(edit.id), edit.values);
      if (!result.level) return result;
      next = result.level;
    } else if (edit.kind === 'trim') {
      const wall = level.walls.find(value => value.id === edit.id), trim = wall?.[edit.trim];
      if (!wall || !trim) return { error: edit.trim === 'plinth' ? t('#PropertyEditPlinthNotFound') : t('#PropertyEditMoldingNotFound') };
      if (!(edit.values.height >= TRIM_HEIGHT_MIN && edit.values.height <= wall.height)) return { error: t('#PropertyEditTrimHeightRange', String(TRIM_HEIGHT_MIN), String(wall.height)) };
      // The app sets the height of every plinth, or every molding, of the room at once (ApplyHeightToAllPlinths).
      const ids = new Set(trimRoomWalls(level, edit.id).filter(value => value[edit.trim]).map(value => value.id));
      next = { ...level, walls: level.walls.map(value => ids.has(value.id) && value[edit.trim]
        ? { ...value, [edit.trim]: { ...value[edit.trim]!, height: Math.min(edit.values.height, value.height) } } : value) };
    } else if (edit.kind === 'wall') {
      const result = applyWallProperties(level, edit.id, edit.values);
      if (!result.level) return result;
      const wall = result.level.walls.find(value => value.id === edit.id);
      next = wall ? { ...result.level, openings: result.level.openings.map(opening =>
        opening.wallId === edit.id && opening.design?.recess !== undefined && opening.design.recess > wall.thickness
          ? { ...opening, design: { ...opening.design, recess: wall.thickness } } : opening) } : result.level;
    } else {
      next = structuredClone(level);
      const opening = next.openings.find(value => value.id === edit.id);
      if (!opening) return { error: t('#GeometryOpeningNotFound') };
      Object.assign(opening, edit.values);
      next = carryCurtains(pairs, syncOpeningTwins(pairs, next, [edit.id]));
      const error = validateLevel(next, level);
      if (error) return { error };
      const wall = next.walls.find(value => value.id === opening.wallId);
      const designError = wall ? validateOpeningDesign(opening, wall) : t('#GeometryWallNotFound');
      if (designError) return { error: designError };
    }
    return { level: JSON.stringify(next) === JSON.stringify(level) ? level : next };
  } catch (failure) {
    return { error: failure instanceof Error ? failure.message : t('#GeometryCheckSizes') };
  }
}

const PLACE_FIELDS = ['x', 'z', 'elevation', 'rotation'] as const;
/** The inspector's size fields of a piece. */
export const SIZE_FIELDS = ['width', 'height', 'depth'];
export interface ReplacementDraft { draft: ItemDraft; baseline: ItemDraft; shown: ShownPlace | null }

/** The inspector's draft once a card is picked to replace the piece: the card's size, and the place a model of another type takes. */
export function pickReplacement(level: Level, item: Item, entry: CatalogItem, { draft, baseline, shown }: ReplacementDraft): ReplacementDraft {
  const size = { w: entry.size[0], h: entry.size[1], d: entry.size[2] };
  const next = shownReplacement(level, item, entry, size);
  const sizes = { width: String(entry.size[0]), height: String(entry.size[1]), depth: String(entry.size[2]) };
  if (!next && !shown) return { draft: { ...draft, ...sizes }, baseline, shown: null };
  // A model of another type rebases the place and its sizes to the card; a later pick of the old type takes back the piece's own.
  const at = itemDraft(next ? { ...item, ...next } : item), fields = Object.fromEntries(PLACE_FIELDS.map(name => [name, at[name]]));
  const own = itemDraft(item);
  return { draft: { ...draft, ...sizes, ...fields }, baseline: { ...baseline, ...fields, ...(next ? sizes : { width: own.width, height: own.height, depth: own.depth }) }, shown: next };
}

/** The draft after `names` changed to `value.draft`; while a replacement's place is shown, a size moves the place with it. */
export function resizeReplacement(level: Level, item: Item, entry: CatalogItem, value: ReplacementDraft, names: readonly string[]): ReplacementDraft {
  const { draft, baseline, shown } = value;
  if (!shown || !names.some(name => SIZE_FIELDS.includes(name))) return value;
  const [w, h, d] = SIZE_FIELDS.map(name => Number(draft[name]?.replace(',', '.')));
  if (![w, h, d].every(size => Number.isFinite(size) && size > 0)) return value;
  const next = shownReplacement(level, item, entry, { w, h, d }, shown.against);
  if (!next) return value;
  const at = itemDraft({ ...item, ...next });
  const followed = Object.fromEntries(PLACE_FIELDS.map(name => [name, draft[name] === baseline[name] ? at[name] : draft[name]]));
  return { draft: { ...draft, ...followed }, baseline: { ...baseline, ...Object.fromEntries(PLACE_FIELDS.map(name => [name, at[name]])) }, shown: next };
}

/** The inspector's edit of a piece: its draft, and a replacement with the place shown for it. */
export function itemEdit(item: Item, { draft, baseline, shown }: ReplacementDraft, replacement: CatalogItem | null, onlyTyped = false): Extract<PropertyEdit, { kind: 'item' }> {
  return { kind: 'item', id: item.id, values: itemDraftValues(shown ? { ...item, ...shown } : item, draft, baseline, onlyTyped), replacement, ...(shown ? { place: shown } : {}) };
}

/** A wall piece's point moved along its wall no further than its ends allow a piece this wide, as the 3D drag stops it; its distance from the wall kept. */
export function withinWall(wall: Wall, point: Vec2, width: number): Vec2 {
  if (width > wall.length) return point;
  const dir = { x: (wall.end.x - wall.start.x) / wall.length, z: (wall.end.z - wall.start.z) / wall.length };
  const along = (point.x - wall.start.x) * dir.x + (point.z - wall.start.z) * dir.z;
  const shift = Math.min(Math.max(along, width / 2), wall.length - width / 2) - along;
  return shift ? { x: point.x + dir.x * shift, z: point.z + dir.z * shift } : point;
}

type ModuleGeometry = Pick<Module, 'name' | 'modelRef' | 'variationId' | 'position' | 'rotation'> & { modules?: ModuleGeometry[] };
function moduleGeometry(module: Module): ModuleGeometry {
  return { name: module.name, modelRef: module.modelRef, variationId: module.variationId,
    position: module.position, rotation: module.rotation, modules: module.modules?.map(moduleGeometry) };
}

/** Geometry that invalidates a pending inspector draft; finishes rebase separately. */
export function itemPropertyGeometry(item: Item): string {
  return JSON.stringify([item.size, item.position, item.elevation, item.rotation, item.modelRef, item.scale, item.mirrored, item.modules?.map(moduleGeometry), item.pivot, item.wallId, item.placement]);
}
